/**
 * Utility for handling menu item API errors and translating them to user-friendly messages
 */

interface BackendErrorResponse {
  error?: {
    type?: string;
    message?: string;
    details?: Array<{ field: string; message: string }>;
  };
}

interface ParsedError {
  userMessage: string;
  fieldErrors: Record<string, string>;
}

/**
 * Maps backend error types to user-friendly messages
 */
const ERROR_TYPE_MESSAGES: Record<string, string> = {
  ValidationError: "Please check your input and try again.",
  NotFoundError:
    "The requested item could not be found. Please refresh and try again.",
  DatabaseError: "A server error occurred. Please try again later.",
  ConflictError:
    "This operation conflicts with existing data. Please check your input and try again.",
  DuplicatePrimaryPhone:
    "A duplicate entry was found. Please check your input.",
  // Database constraint error codes (from PostgreSQL)
  "22001": "One or more fields are too long. Please shorten your input.",
  "23505":
    "This operation conflicts with existing data. Please check your input and try again.",
  "23503": "Invalid reference. Please check that all referenced items exist.",
  "23502": "Required field is missing. Please check your input.",
  "23514": "Invalid data format. Please check your input values.",
  "22P02": "Invalid input format. Please verify your input values.",
};

/**
 * Maps specific error messages to user-friendly alternatives
 */
const ERROR_MESSAGE_MAPPINGS: Record<string, string> = {
  // String truncation errors
  "One or more fields are too long. Please shorten your input.":
    "One or more fields exceed the maximum length. Please shorten your input.",
  string_data_right_truncation:
    "One or more fields are too long. Please reduce the length of your text.",

  // Field-specific messages
  "Missing required fields": "Please fill in all required fields.",
  "Item name is required": "Item name is required.",
  "Price is required": "Price is required.",

  // Not found errors
  "Restaurant not found or does not belong to this hotel":
    "The restaurant could not be found. Please refresh the page.",
  "Menu not found for this restaurant":
    "The menu could not be found. Please refresh the page.",
  "Menu item not found for your hotel":
    "The menu item could not be found. Please refresh the page.",
  "Room service menu not found for your hotel":
    "The room service menu could not be found. Please refresh the page.",
  "Room service item not found for this menu":
    "The room service item could not be found. Please refresh the page.",
  "Price not found for your hotel":
    "The price information could not be found. Please try again.",

  // Database errors
  "Failed to create menu item.":
    "Unable to create the menu item. Please try again.",
  "Failed to update menu item":
    "Unable to update the menu item. Please try again.",
  "Failed to create price record":
    "Unable to create the price. Please try again.",

  // Foreign key constraint errors (for room service items)
  "Cannot delete room service item":
    "This item cannot be deleted because it is referenced in existing orders.",

  // Generic fallbacks
  "Invalid reference. Please check that all referenced items exist.":
    "One or more references are invalid. Please check your selections.",
};

/**
 * Maps field names to user-friendly labels
 */
const FIELD_LABEL_MAP: Record<string, string> = {
  itemname: "Item Name",
  description: "Description",
  ingredients: "Ingredients",
  category: "Category",
  price: "Price",
  priceAmount: "Price Amount",
  currencyCode: "Currency",
  photo: "Photo",
  restaurantmenuid: "Menu",
  roomservicemenuid: "Room Service Menu",
  priceid: "Price",
};

/**
 * Extracts and translates backend errors to user-friendly format
 */
export function parseMenuItemError(error: any): ParsedError {
  const fieldErrors: Record<string, string> = {};
  let userMessage = "An error occurred. Please try again.";

  // Check if error has a response structure (Axios error)
  const errorResponse: BackendErrorResponse | undefined = error?.response?.data;

  if (errorResponse?.error) {
    const backendError = errorResponse.error;
    const errorType = backendError.type || "";
    const errorMessage = backendError.message || "";

    // Handle field-specific validation errors
    if (backendError.details && Array.isArray(backendError.details)) {
      backendError.details.forEach((detail) => {
        if (detail.field && detail.message) {
          const fieldLabel = FIELD_LABEL_MAP[detail.field] || detail.field;

          // Map error message to user-friendly version
          let friendlyMessage = detail.message;

          // Check for specific field error patterns
          if (
            detail.message.includes("too long") ||
            detail.message.includes("exceed")
          ) {
            friendlyMessage = `${fieldLabel} is too long. Please shorten it.`;
          } else if (detail.message.includes("required")) {
            friendlyMessage = `${fieldLabel} is required.`;
          } else if (detail.message.includes("must be")) {
            // Keep the message but make it more readable
            friendlyMessage = `${fieldLabel}: ${detail.message}`;
          }

          fieldErrors[detail.field] = friendlyMessage;
        }
      });

      // If we have field errors, set a general message
      if (Object.keys(fieldErrors).length > 0) {
        userMessage = "Please fix the highlighted fields below.";
      }
    }

    // Handle error type messages
    if (ERROR_TYPE_MESSAGES[errorType]) {
      userMessage = ERROR_TYPE_MESSAGES[errorType];
    }

    // Handle specific error messages (override type-based messages if message is more specific)
    if (errorMessage && ERROR_MESSAGE_MAPPINGS[errorMessage]) {
      userMessage = ERROR_MESSAGE_MAPPINGS[errorMessage];
    } else if (
      errorMessage &&
      (!fieldErrors || Object.keys(fieldErrors).length === 0)
    ) {
      // Use the backend message if we don't have a mapping and no field errors
      // But filter out technical details
      userMessage = sanitizeErrorMessage(errorMessage);
    }

    // If we have a "too long" message but no field details, try to infer from request data
    if (
      (errorMessage.includes("too long") || userMessage.includes("too long")) &&
      Object.keys(fieldErrors).length === 0
    ) {
      // Try to get field info from request data
      const requestData = error?.config?.data;
      if (requestData) {
        try {
          const data =
            typeof requestData === "string"
              ? JSON.parse(requestData)
              : requestData;

          // Check common fields that might be too long (typical max lengths)
          const lengthChecks = [
            { field: "itemname", maxLength: 255 },
            { field: "description", maxLength: 2000 },
            { field: "ingredients", maxLength: 2000 },
            { field: "category", maxLength: 100 },
          ];

          for (const check of lengthChecks) {
            if (data[check.field] && String(data[check.field]).length > 200) {
              // If field is reasonably long, flag it as likely culprit
              const fieldLabel = FIELD_LABEL_MAP[check.field] || check.field;
              fieldErrors[
                check.field
              ] = `${fieldLabel} is too long. Please shorten it.`;
              userMessage = `${fieldLabel} is too long. Please reduce the length.`;
              break;
            }
          }
        } catch (e) {
          // If we can't parse, continue with generic message
        }
      }
    }
  } else if (error?.code) {
    // Handle database error codes directly
    if (ERROR_TYPE_MESSAGES[error.code]) {
      userMessage = ERROR_TYPE_MESSAGES[error.code];

      // Try to extract field name from database error detail if available
      if (error.code === "22001" && error.detail) {
        // PostgreSQL detail format: "value too long for type character varying(255)"
        // or may contain column name in some cases
        const detail = String(error.detail).toLowerCase();

        // Try to match common field patterns in the error detail
        const fieldMatches = [
          { pattern: /column\s+["']?(\w+)["']?/i, name: "itemname" },
          { pattern: /itemname/i, name: "itemname" },
          { pattern: /description/i, name: "description" },
          { pattern: /ingredients/i, name: "ingredients" },
          { pattern: /category/i, name: "category" },
        ];

        for (const match of fieldMatches) {
          if (match.pattern.test(detail)) {
            const fieldLabel = FIELD_LABEL_MAP[match.name] || match.name;
            fieldErrors[
              match.name
            ] = `${fieldLabel} is too long. Please shorten it.`;
            userMessage = `${fieldLabel} is too long. Please reduce the length.`;
            break;
          }
        }
      }
    }
  } else if (error?.message) {
    // Fallback to error message if available
    userMessage = sanitizeErrorMessage(error.message);
  }

  // If we have a generic "too long" message but no field errors, try to infer from request data
  if (
    userMessage.includes("too long") &&
    Object.keys(fieldErrors).length === 0 &&
    error?.config?.data
  ) {
    try {
      const requestData =
        typeof error.config.data === "string"
          ? JSON.parse(error.config.data)
          : error.config.data;

      // Check common fields that might be too long
      const lengthChecks = [
        { field: "itemname", maxLength: 255 },
        { field: "description", maxLength: 2000 },
        { field: "ingredients", maxLength: 2000 },
        { field: "category", maxLength: 100 },
      ];

      for (const check of lengthChecks) {
        if (
          requestData[check.field] &&
          String(requestData[check.field]).length > check.maxLength * 0.9
        ) {
          // If field is close to or exceeds typical max length, flag it
          const fieldLabel = FIELD_LABEL_MAP[check.field] || check.field;
          fieldErrors[
            check.field
          ] = `${fieldLabel} is too long. Please shorten it.`;
          userMessage = `${fieldLabel} is too long. Please reduce the length.`;
          break;
        }
      }
    } catch (e) {
      // If we can't parse, just use the generic message
    }
  }

  // Special handling for foreign key constraint errors on delete
  if (
    error?.response?.data?.error?.message?.includes(
      "Cannot delete room service item"
    ) &&
    error?.response?.data?.error?.message?.includes("referenced in")
  ) {
    const match = error.response.data.error.message.match(
      /referenced in (\d+) existing dining order/
    );
    const orderCount = match ? parseInt(match[1], 10) : 0;
    userMessage = `Cannot delete this item. It is currently referenced in ${orderCount} existing order(s). You can set it to unavailable instead.`;
  }

  return {
    userMessage,
    fieldErrors,
  };
}

/**
 * Sanitizes error messages to remove technical details
 */
function sanitizeErrorMessage(message: string): string {
  // Remove common technical patterns
  let sanitized = message
    // Remove SQL error details
    .replace(/SQLSTATE\[.*?\]/g, "")
    .replace(/SQL.*?ERROR/g, "")
    // Remove constraint names
    .replace(/constraint\s+"[^"]+"/gi, "")
    // Remove database column references in quotes
    .replace(/column\s+"[^"]+"/gi, "")
    // Remove UUID patterns that might be exposed
    .replace(
      /[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}/gi,
      "[ID]"
    )
    // Remove file paths
    .replace(/\/[^\s]+/g, "[PATH]")
    // Remove stack trace indicators
    .replace(/at\s+.*/g, "")
    // Clean up multiple spaces
    .replace(/\s+/g, " ")
    .trim();

  // If message is empty or too technical, return generic message
  if (!sanitized || sanitized.length < 5) {
    return "An error occurred. Please try again.";
  }

  // Capitalize first letter
  return sanitized.charAt(0).toUpperCase() + sanitized.slice(1);
}

/**
 * Hook-style function to handle menu item errors and update state
 */
export function handleMenuItemError(
  error: any,
  setUpdateError: (message: string | null) => void,
  setFieldErrors: (errors: Record<string, string>) => void
): void {
  const parsed = parseMenuItemError(error);

  setUpdateError(parsed.userMessage);
  setFieldErrors(parsed.fieldErrors);
}
